import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { usePhoneDetails } from 'v2/pages/settings/security/queries'
import { useAuth } from 'v2/shared/hooks/use-auth'
import type { RCard } from 'v2/types/api/billing'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { logger } from 'v2/utils/logger'
import { errorToast, infoToast, successToast } from 'v2/utils/toasts'
import {
  useAccountDetailsQuery,
  useRazorpayCreateOrderMutation,
  useRazorpayVerifyPaymentMutation,
} from '../queries'
import {
  getRazorpay,
  launchRazorpayCheckout,
  RAZORPAY_CARD_ONLY_CONFIG,
  RAZORPAY_NETBANKING_ONLY_CONFIG,
  type RazorpayCheckoutOptions,
} from '../razorpay-sdk'
import {
  BILLING_REFRESH_PROCESSING_MAX_POLL_ATTEMPTS,
  type BillingRefreshOptions,
  refreshBillingAfterPayment,
} from '../refresh-billing-queries'
import { runTrialUpgradeFollowUp, shouldRunTrialFollowUp } from '../trial-upgrade-followup'
import { buildUpgradePlanPatch } from '../upgrade-plan'
import { getAccountId } from '../utils'

// stripe_client marker payment-service stamps on cards tokenised with Razorpay
// (plivo_billing_accountcardmapping.stripe_client). It is the provider
// discriminator on the shared cards table — NOT payment_description.
export const RAZORPAY_STRIPE_CLIENT = 'RAZORPAY'

// A saved card belongs to the Razorpay gateway. Such a card must be charged via
// the hosted Razorpay Standard Checkout — regardless of the account's currency,
// because the server-side /recharge and Cashfree S2S paths can't authenticate a
// Razorpay token. Used by every recharge modal so the routing predicate can't
// drift between them.
export const isRazorpaySavedCard = (card?: Pick<RCard, 'stripe_client'>): boolean =>
  card?.stripe_client === RAZORPAY_STRIPE_CLIENT

// Gateway label for the RECHARGE_INITIATED analytics event. The card's own client
// wins over the account-level provider: a RAZORPAY card routes to Standard
// Checkout, so labelling it by provider would report the funnel against the wrong
// gateway. Shared by both recharge hosts so their labels can't drift.
export const rechargeGatewayLabel = (
  card: Pick<RCard, 'stripe_client'> | undefined,
  isCashfreeProvider: boolean,
): 'razorpay' | 'cashfree' | 'stripe' => {
  if (isRazorpaySavedCard(card)) return 'razorpay'
  return isCashfreeProvider ? 'cashfree' : 'stripe'
}

// Pull the human-readable message the payment-service returns out of an axios
// error. The backend error body is structs.Response — `message` on the Razorpay
// endpoints (a few use `error`) — so surfacing it gives the user the real reason
// ("Only INR…", "Amount must be between…", etc.) instead of a generic failure.
// Falls back to the caller's default. (Kept identical to the copy in
// razorpay-recharge-modal.tsx so both launch paths behave the same.)
export const razorpayApiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { message?: string; error?: string } } })?.response
    ?.data
  return data?.message || data?.error || fallback
}

interface UseRazorpayStandardCheckoutParams {
  // Called AFTER a completed (verified) payment to reset + close the host modal.
  // Must be the host's UNGUARDED reset-and-close: it runs from inside the verify
  // handler's `finally`, so a close guarded on "is launching" would be blocked by
  // the not-yet-flushed state update and the modal would never close.
  onClose: () => void
  // Warm the Razorpay CDN script when true so the hosted modal launches instantly
  // on Pay instead of cold-loading after the order-create round-trip. Hosts pass
  // `isOpen && hasRazorpayCard` so the script isn't fetched for accounts that will
  // never open it. getRazorpay() is memoized + idempotent.
  warm?: boolean
  // Load-bearing, not just analytics. Reported on the CREDITS_RECHARGED event so
  // the trial-vs-paid split matches the /recharge path's payload, AND it decides
  // the order's `upgrade_plan` — a trial account's first payment is what buys the
  // Professional plan, and only the capture webhook can flip it (see
  // ../upgrade-plan.ts).
  isTrial?: boolean
}

interface LaunchStandardCheckoutOptions {
  // When true (Payment Methods "Add Card"), a save_card order that comes back with
  // no razorpay_customer_id is treated as a failure — the whole point is vaulting
  // the card. For a plain recharge it proceeds without advertising a save.
  isAddCardIntent?: boolean
  // Which method the hosted modal is pinned to. Defaults to 'card'. Netbanking
  // has nothing to vault, so it skips the customer/save options, and the backend
  // skips the gateway fee for it — which is also why the modal must be PINNED to
  // the method the order was priced for (display.blocks; Standard Checkout has no
  // top-level `method` option). Letting the customer switch inside the modal
  // would charge an amount the order wasn't priced for.
  method?: 'card' | 'netbanking'
}

// Encapsulates the hosted Razorpay Standard Checkout launch used to charge/vault a
// card: order-create (with save_card + customer prefill) → the requestAnimationFrame
// launch that runs only after the host Dialog has committed its non-modal re-render
// → verify + billing-query refresh. Extracted so the generic and Cashfree
// recharge modals — the USD/trial and netbanking surfaces, which are not the
// Razorpay checkout — can still charge a RAZORPAY-client saved card.
//
// HOST CONTRACT: bind `modal={!isLaunching}` on the host <Dialog> and block the
// dialog's user-initiated close while `isLaunching` — otherwise Razorpay's hosted
// iframe (appended to <body>) lands inside Radix's inert region and is
// unfocusable/unclickable, or the host closes out from under the open checkout.
export const useRazorpayStandardCheckout = ({
  onClose,
  warm = false,
  isTrial = false,
}: UseRazorpayStandardCheckoutParams) => {
  const accountId = getAccountId()
  const { details } = useAuth()
  // The logged-in user's phone (from the 2FA/onboarding record) — used only to
  // prefill the checkout contact field. Omitted if unavailable (Razorpay lets the
  // user enter it, and the backend resolves the real contact for the saved
  // customer regardless).
  const { data: phoneDetailsData } = usePhoneDetails()
  const { data: accountDetails } = useAccountDetailsQuery()
  // Name/email from the profile endpoint (same source as the Profile page) — the
  // session name is often empty. Falls back to account/session values.
  const { data: profileData, refetch: refetchProfile } = useProfileQuery()
  const queryClient = useQueryClient()
  const { mutateAsync: createOrder, isPending: isCreatingOrder } = useRazorpayCreateOrderMutation()
  const { mutateAsync: verifyPayment } = useRazorpayVerifyPaymentMutation()

  const [isLaunching, setIsLaunching] = useState(false)
  // Hosted-checkout options staged by launchStandardCheckout, launched by the
  // effect below — deferred so the Dialog has re-rendered non-modal first.
  const [pendingCheckout, setPendingCheckout] = useState<RazorpayCheckoutOptions | null>(null)

  const prefillContact =
    phoneDetailsData?.data?.body?.verified_phone_number ||
    phoneDetailsData?.data?.body?.unverified_phone_number ||
    undefined
  const customerName =
    `${profileData?.account_first_name ?? ''} ${profileData?.account_last_name ?? ''}`.trim() ||
    profileData?.account_name ||
    accountDetails?.account?.name ||
    accountDetails?.account?.company ||
    details?.name
  const customerEmail = profileData?.email || details?.email

  // Warm the Razorpay SDK on open so the hosted modal launches instantly on Pay
  // instead of cold-loading the CDN script after the order-create round-trip.
  useEffect(() => {
    if (warm) getRazorpay().catch(() => {})
  }, [warm])

  // The credit (and a newly-vaulted card) lands via the async payment.captured
  // webhook, AFTER verify returns — so this polls until the value actually moves
  // instead of invalidating once against a balance the backend hasn't updated yet.
  const refreshBillingQueries = useCallback(
    (options?: BillingRefreshOptions) => {
      void refreshBillingAfterPayment(queryClient, accountId, options)
    },
    [queryClient, accountId],
  )

  // Launch the hosted checkout only AFTER the Dialog has committed its non-modal
  // re-render (host binds modal={!isLaunching}). Flipping modal false synchronously
  // tears down Radix's focus-trap + aria-hidden + pointer-events lock; running the
  // launch in a post-commit effect + rAF guarantees that teardown has hit the DOM
  // before Razorpay's iframe mounts — otherwise the iframe lands inside an inert
  // region and is unfocusable/unclickable.
  useEffect(() => {
    if (!pendingCheckout) return
    const raf = requestAnimationFrame(() => {
      launchRazorpayCheckout(pendingCheckout, (payload) => {
        setIsLaunching(false)
        logger.error('useRazorpayStandardCheckout: payment.failed', { payload })
        // Razorpay's payment.failed payload carries error.description (the
        // gateway/bank reason) — show it so the user knows why it failed.
        const description =
          (payload as { error?: { description?: string } })?.error?.description ||
          'Please try again.'
        errorToast({ title: 'Payment Failed', description })
      }).catch((error) => {
        setIsLaunching(false)
        logger.error('useRazorpayStandardCheckout: launch failed', { error })
        errorToast({
          title: 'Payment Failed',
          description: 'We faced an error. Please try again in some time.',
        })
      })
      setPendingCheckout(null)
    })
    return () => cancelAnimationFrame(raf)
  }, [pendingCheckout])

  const launchStandardCheckout = useCallback(
    async (amount: number, opts: LaunchStandardCheckoutOptions = {}) => {
      const { isAddCardIntent = false, method = 'card' } = opts
      const isCard = method === 'card'
      if (!amount || amount <= 0) return
      let order: Awaited<ReturnType<typeof createOrder>>
      try {
        order = await createOrder({
          account_id: accountId,
          amount,
          currency: 'INR',
          product_type: 'plivo',
          payment_method: method,
          save_card: isCard,
          // Send the customer's name/email/phone so the Razorpay customer is
          // created with real details instead of the backend's synthetic fallback.
          customer_name: customerName,
          customer_email: customerEmail,
          customer_phone: prefillContact,
          // Trial account: this payment buys Professional. Razorpay stashes it in
          // the order Notes and order.paid upgrades the plan after crediting.
          ...buildUpgradePlanPatch(isTrial),
        })
      } catch (error) {
        logger.error('useRazorpayStandardCheckout: order create failed', { error })
        errorToast({
          title: 'Payment Failed',
          description: razorpayApiErrorMessage(
            error,
            'We faced an error. Please try again in some time.',
          ),
        })
        return
      }
      if (!order.razorpay_order_id || !order.key_id) {
        errorToast({
          title: isCard ? 'Card payments are not configured' : 'Netbanking is not configured',
          description: 'Please contact support.',
        })
        return
      }
      // The backend provisions the Razorpay customer best-effort: a save_card order
      // can succeed (200) yet return NO razorpay_customer_id. Without it we cannot
      // pass customer_id + remember_customer, so the card would NOT be vaulted.
      const canSaveCard = isCard && !!order.razorpay_customer_id
      if (isAddCardIntent && !canSaveCard) {
        logger.error('useRazorpayStandardCheckout: save_card order missing razorpay_customer_id')
        errorToast({
          title: 'Could not add card',
          description: 'We could not set up card saving right now. Please try again.',
        })
        return
      }

      const checkoutOptions: RazorpayCheckoutOptions = {
        key: order.key_id,
        order_id: order.razorpay_order_id,
        amount: order.amount_paise,
        currency: order.currency || 'INR',
        name: 'Plivo',
        description: 'Add credits',
        // Pin the modal to the method the order was priced for.
        config: isCard ? RAZORPAY_CARD_ONLY_CONFIG : RAZORPAY_NETBANKING_ONLY_CONFIG,
        // Save the card at checkout only when the backend gave us a customer id:
        // customer_id + remember_customer=true (per Razorpay Checkout Options docs).
        // Not `recurring` (that's mandate/S2S).
        ...(canSaveCard
          ? { customer_id: order.razorpay_customer_id, remember_customer: true, save: 1 }
          : {}),
        // Prefill checkout with the customer's name/email and real phone (when
        // available). contact is omitted when unknown rather than faked.
        prefill: {
          name: details?.name,
          email: details?.email,
          ...(prefillContact ? { contact: prefillContact } : {}),
        },
        handler: async (response) => {
          try {
            const verification = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            })
            // Track the card too when this checkout was vaulting one; a
            // not-yet-captured payment gets the longer window because the bank
            // confirmation it is waiting on is the slow part.
            refreshBillingQueries({
              targets: canSaveCard
                ? ['balance', 'cards', 'transactions']
                : ['balance', 'transactions'],
              ...(verification.captured === false
                ? { maxAttempts: BILLING_REFRESH_PROCESSING_MAX_POLL_ATTEMPTS }
                : {}),
            })
            // Downgrade to "processing" only when the payment is explicitly not yet
            // captured; verified with captured true/undefined = success (the handler
            // fires only on a successful, auto-captured payment).
            if (!verification.verified) {
              errorToast({ title: 'Payment Failed', description: 'Please try again.' })
            } else if (verification.captured === false) {
              // Verified but not yet captured. The plan flip rides on the capture
              // webhook, so this branch has an upgrade STILL COMING and needs the
              // follow-up more than the captured one — see shouldRunTrialFollowUp.
              // CREDITS_RECHARGED stays out of it, the same way a pending 3DS
              // redirect doesn't count as recharged.
              if (shouldRunTrialFollowUp(verification)) {
                runTrialUpgradeFollowUp({
                  isTrial,
                  refetchProfile,
                  currentPlan: profileData?.plan,
                  amount,
                  currency: 'INR',
                  method,
                })
              }
              infoToast({
                title: 'Payment processing',
                description: 'Your payment is being confirmed and will reflect shortly.',
              })
            } else {
              // Completion event for the funnel. Routing a card here bypasses the
              // /recharge success callback that used to emit it, so without this a
              // successful Razorpay recharge reports an initiation and no
              // completion. Not emitted for the not-yet-captured branch above, the
              // same way a pending 3DS redirect doesn't count as recharged. Add-card
              // has its own event story, so it stays out of the recharge funnel.
              if (!isAddCardIntent) {
                track(AnalyticsEvents.CREDITS_RECHARGED, {
                  amount,
                  currency: 'INR',
                  is_trial: isTrial,
                  gateway: 'razorpay',
                })
              }
              // Trial account: the order carried upgrade_plan, so payment-service
              // flips the plan from its order.paid webhook — AFTER this handler.
              // The shared follow-up polls the profile until it moves and emits
              // PLAN_UPGRADED when it does; see ../trial-upgrade-followup for why
              // that event cannot be left to useChangePlanMutation any more.
              runTrialUpgradeFollowUp({
                isTrial,
                refetchProfile,
                currentPlan: profileData?.plan,
                amount,
                currency: 'INR',
                method,
              })
              successToast({ title: 'Success', description: 'Payment successful' })
            }
          } catch (error) {
            logger.error('useRazorpayStandardCheckout: verify failed', { error })
            errorToast({
              title: 'Payment Failed',
              description: razorpayApiErrorMessage(error, 'Please try again.'),
            })
          } finally {
            setIsLaunching(false)
            onClose()
          }
        },
        modal: {
          // Quiet cancel — the user backed out; not a failure. Host modal stays open.
          ondismiss: () => setIsLaunching(false),
        },
      }
      setIsLaunching(true)
      setPendingCheckout(checkoutOptions)
    },
    [
      accountId,
      customerName,
      customerEmail,
      prefillContact,
      details,
      createOrder,
      verifyPayment,
      refreshBillingQueries,
      onClose,
      isTrial,
      refetchProfile,
      profileData?.plan,
    ],
  )

  return { launchStandardCheckout, isLaunching, isCreatingOrder }
}
